// src/pages/DraftList.js
import React, { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce"; // For debounced search
import { format } from "date-fns";

function DraftList() {
  // 1) AUTH & BASIC STATE
  const authData = localStorage.getItem("authData");
  const parsedAuthData = authData ? JSON.parse(authData) : null;
  const userId = parsedAuthData?.user?.sub;

  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 2) PAGINATION STATE
  const [tokens, setTokens] = useState([null]); // tokens[0] = initial page
  const [currentPage, setCurrentPage] = useState(0);
  const [nextToken, setNextToken] = useState(null);

  // 3) SEARCH STATE
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 4) FILTER & SORTING
  // Change here: default to "all" instead of "favorite"
  const [filterOption, setFilterOption] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });

  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION HELPER (CLIENT-SIDE CHECK)
  // --------------------------------------------------------------------------
  const isUserSessionValid = () => {
    if (!parsedAuthData) return false;
    return true;
  };

  // --------------------------------------------------------------------------
  // MAIN FETCH FUNCTION
  // --------------------------------------------------------------------------
  const fetchDrafts = async (pageIndex, search = "") => {
    if (!isUserSessionValid()) {
      //console.warn("Session invalid. Redirecting to callback...");
      navigate("/callback");
      return;
    }

    const token = tokens[pageIndex] || null;
    setIsLoading(true);
    setError(null);

    try {
      const API_URL = search
        ? process.env.REACT_APP_SEARCH_URL
        : process.env.REACT_APP_ALL_DRAFTS_URL;

      const payload = search
        ? { userId, searchQuery: search, nextToken: token }
        : { userId, nextToken: token };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.status === 401 || response.status === 403) {
        //console.error("Session expired or not authorized. Redirecting...");
        navigate("/callback");
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch drafts: ${errorText}`);
      }

      const data = await response.json();
      const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      const normalizedDrafts = (parsedBody.drafts || []).map((draft) => ({
        ...draft,
        isDefault: draft.isDefault === "Y",
        isFavorite: draft.isFavorite === "Y",
      }));

      setDrafts(normalizedDrafts);
      setNextToken(parsedBody.nextToken || null);

      if (pageIndex === tokens.length - 1 && parsedBody.nextToken) {
        setTokens((prev) => [...prev, parsedBody.nextToken]);
      }
    } catch (err) {
      //console.error("Error Fetching Drafts:", err);
      setError("Failed to load drafts. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // PAGINATION HANDLERS
  // --------------------------------------------------------------------------
  const handleNext = () => {
    if (!isUserSessionValid()) {
      navigate("/callback");
      return;
    }
    if (nextToken) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchDrafts(newPage, debouncedSearch);
    }
  };

  const handlePrevious = () => {
    if (!isUserSessionValid()) {
      navigate("/callback");
      return;
    }
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchDrafts(newPage, debouncedSearch);
    }
  };

  // --------------------------------------------------------------------------
  // INITIAL / SUBSEQUENT FETCH
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!userId) {
      setError("You are not logged in. Please log in to access drafts.");
      return;
    }
    setTokens([null]);
    setCurrentPage(0);
    fetchDrafts(0, debouncedSearch);
  }, [userId, debouncedSearch]);

  // --------------------------------------------------------------------------
  // UPDATE DRAFT FIELDS
  // --------------------------------------------------------------------------
  const updateDraft = async (draftId, updatedFields) => {
    const UPDATE_API_URL =
      process.env.REACT_APP_ALL_DRAFTS_URL;

    try {
      const response = await fetch(UPDATE_API_URL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, draftId, ...updatedFields }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update draft: ${errorText}`);
      }
    } catch (err) {
      //console.error("Error Updating Draft:", err);
      setError("Failed to update draft. Please try again later.");
    }
  };

  const handleCheckboxChange = (draftId, field) => {
    if (field === "isDefault") {
      setDrafts((prevDrafts) =>
        prevDrafts.map((draft) => ({
          ...draft,
          isDefault: draft.id === draftId,
          isFavorite: draft.id === draftId ? true : draft.isFavorite,
        }))
      );

      drafts.forEach((draft) => {
        const isDefaultUpdate = draft.id === draftId ? "Y" : "N";
        const isFavoriteUpdate =
          draft.id === draftId ? "Y" : draft.isFavorite ? "Y" : "N";

        updateDraft(draft.id, {
          isDefault: isDefaultUpdate,
          isFavorite: isFavoriteUpdate,
        });
      });
    } else if (field === "isFavorite") {
      setDrafts((prevDrafts) =>
        prevDrafts.map((draft) => {
          if (draft.id === draftId && draft.isDefault) {
            return draft;
          }
          return draft.id === draftId ? { ...draft, isFavorite: !draft.isFavorite } : draft;
        })
      );

      const draftToUpdate = drafts.find((d) => d.id === draftId);
      if (draftToUpdate && !draftToUpdate.isDefault) {
        updateDraft(draftId, {
          isFavorite: draftToUpdate.isFavorite ? "N" : "Y",
        });
      }
    }
  };

  const handleNicknameChange = (draftId, newNickname) => {
    setDrafts((prevDrafts) =>
      prevDrafts.map((draft) =>
        draft.id === draftId ? { ...draft, nickName: newNickname } : draft
      )
    );
    updateDraft(draftId, { nickName: newNickname });
  };

  // --------------------------------------------------------------------------
  // DEBOUNCED SEARCH
  // --------------------------------------------------------------------------
  const debouncedChange = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearch(value);
      }, 300),
    []
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedChange(e.target.value);
  };

  const handleReset = () => {
    setSearchQuery("");
    debouncedChange.cancel();
    setDebouncedSearch("");
  };

  useEffect(() => {
    return () => {
      debouncedChange.cancel();
    };
  }, [debouncedChange]);

  // --------------------------------------------------------------------------
  // DOWNLOAD (UNCHANGED)
  // --------------------------------------------------------------------------
  const handleDownload = async (draftId) => {
    try {
      setIsLoading(true);
      const payload = { id: draftId, userId };
      const response = await fetch(process.env.REACT_APP_DOWNLOAD_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.status === 401 || response.status === 403) {
        //console.error("Session expired while downloading. Redirecting...");
        navigate("/callback");
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download draft: ${errorText}`);
      }

      const data = await response.json();
      const parsedBody =
        typeof data.body === "string" ? JSON.parse(data.body) : data.body;
      const { draft, personalizedDraft } = parsedBody;

      const combinedContent = `
----- ORIGINAL DRAFT -----
${draft}

----- PERSONALIZED DRAFT -----
${personalizedDraft}
      `.trim();

      const blob = new Blob([combinedContent], { type: "text/plain;charset=utf-8" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `draft-${draftId}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      //console.error("Error downloading draft:", error);
      setError("Failed to download draft. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // MEMOIZED DRAFTS (FILTER, OPTIONAL LOCAL SEARCH, SORT)
  // --------------------------------------------------------------------------
  const displayedDrafts = useMemo(() => {
    let filtered = [...drafts];

    // Now we filter only if filterOption === "favorite"
    // If filterOption === "all", we leave them as is.
    if (filterOption === "favorite") {
      filtered = filtered.filter((d) => d.isFavorite);
    }

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((d) => {
        const draftText = d.draft?.toLowerCase() || "";
        const nicknameText = d.nickName?.toLowerCase() || "";
        return draftText.includes(q) || nicknameText.includes(q);
      });
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "createdAt") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else {
          aValue = aValue ? aValue.toString().toLowerCase() : "";
          bValue = bValue ? bValue.toString().toLowerCase() : "";
        }

        if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [drafts, filterOption, sortConfig, searchQuery]);

  // --------------------------------------------------------------------------
  // SORTING UTILS
  // --------------------------------------------------------------------------
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "ascending" ? <span> ▲</span> : <span> ▼</span>;
  };

  function formatDate(dateString) {
    return format(new Date(dateString), "MM-dd-yyyy");
  }

  const truncateDraft = (draftText) => {
    const words = draftText.split(/\s+/);
    if (words.length <= 50) return draftText;
    return words.slice(0, 50).join(" ") + "...";
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-6 sm:p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6">Your Drafts</h1>

        {/* FILTER */}
        <div className="mb-4 flex flex-col sm:flex-row items-center">
          <label htmlFor="draftFilter" className="mr-2 font-medium mb-2 sm:mb-0">
            Show:
          </label>
          <select
            id="draftFilter"
            value={filterOption}
            onChange={(e) => setFilterOption(e.target.value)}
            className="p-2 border rounded-md w-full sm:w-auto"
          >
            <option value="favorite">Favorite Drafts</option>
            <option value="all">All Drafts</option>
          </select>
        </div>
        <div>
          <p className="text-sm text-gray-500 italic">
            (See New Draft if you do not have any Drafts in this list)
          </p>
        </div>

        {/* SEARCH + RESET */}
        <div className="mb-4 flex flex-col sm:flex-row items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search drafts or nicknames..."
            className="flex-grow p-2 border rounded-md mb-2 sm:mb-0"
          />
          {searchQuery && (
            <button
              onClick={handleReset}
              className="ml-0 sm:ml-2 p-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              aria-label="Reset Search"
            >
              Reset
            </button>
          )}
        </div>

        {/* ERROR MESSAGE */}
        {error && <p className="text-red-500 mb-6">{error}</p>}

        {/* LOADING INDICATOR */}
        {isLoading && (
          <div className="flex items-center mb-6">
            <div className="spinner-border animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
            <span className="ml-2 text-blue-500">Loading...</span>
          </div>
        )}

        {/* RESPONSIVE CARD (MOBILE) */}
        <div className="block sm:hidden">
          {displayedDrafts.length > 0 ? (
            displayedDrafts.map((draft) => (
              <div
                key={draft.id}
                className="mb-4 p-4 border rounded-md shadow-sm bg-gray-50"
              >
                <div className="mb-2">
                  <strong>Draft:</strong> {truncateDraft(draft.draft)}
                </div>
                <div className="mb-2">
                  <strong>Nickname:</strong>
                  <input
                    type="text"
                    value={draft.nickName || ""}
                    onChange={(e) => handleNicknameChange(draft.id, e.target.value)}
                    className="ml-2 p-1 border rounded-md w-full"
                    placeholder="Enter nickname"
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-2">
                  <strong>Created:</strong>{" "}
                  {draft.createdAt ? formatDate(draft.createdAt) : "No Date"}
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={draft.isDefault}
                      onChange={() => handleCheckboxChange(draft.id, "isDefault")}
                      disabled={isLoading}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-1">Default</span>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={draft.isFavorite}
                      disabled={draft.isDefault || isLoading}
                      onChange={() => handleCheckboxChange(draft.id, "isFavorite")}
                      className="form-checkbox h-5 w-5 text-yellow-500"
                    />
                    <span className="ml-1">Favorite</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            !isLoading && <div className="text-center py-4">No drafts found.</div>
          )}
        </div>

        {/* TABLE LAYOUT (SM+) */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full border-collapse mb-6">
            <thead>
              <tr>
                <th
                  className="px-4 py-2 border-b cursor-pointer"
                  onClick={() => handleSort("draft")}
                >
                  Draft{renderSortIndicator("draft")}
                </th>
                <th
                  className="px-4 py-2 border-b cursor-pointer"
                  onClick={() => handleSort("nickName")}
                >
                  Nickname{renderSortIndicator("nickName")}
                </th>
                <th
                  className="px-4 py-2 border-b cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                  style={{ width: "200px" }}
                >
                  Created{renderSortIndicator("createdAt")}
                </th>
                <th className="px-4 py-2 border-b">Default</th>
                <th className="px-4 py-2 border-b">Favorite</th>
                <th className="px-4 py-2 border-b"></th>
              </tr>
            </thead>
            <tbody>
              {displayedDrafts.length > 0 ? (
                displayedDrafts.map((draft) => (
                  <tr key={draft.id}>
                    <td className="px-4 py-2 border-b" title={draft.draft}>
                      {truncateDraft(draft.draft)}
                    </td>
                    <td className="px-4 py-2 border-b">
                      <input
                        type="text"
                        value={draft.nickName || ""}
                        onChange={(e) => handleNicknameChange(draft.id, e.target.value)}
                        className="p-2 border rounded-md w-full"
                        placeholder="Enter nickname"
                        disabled={isLoading}
                      />
                    </td>
                    <td className="px-4 py-2 border-b" style={{ width: "200px" }}>
                      {draft.createdAt ? formatDate(draft.createdAt) : "No Date"}
                    </td>
                    <td className="px-4 py-2 border-b text-center">
                      <input
                        type="checkbox"
                        checked={draft.isDefault}
                        onChange={() => handleCheckboxChange(draft.id, "isDefault")}
                        disabled={isLoading}
                        className="form-checkbox h-5 w-5 text-blue-600"
                      />
                    </td>
                    <td className="px-4 py-2 border-b text-center">
                      <input
                        type="checkbox"
                        checked={draft.isFavorite}
                        disabled={draft.isDefault || isLoading}
                        onChange={() => handleCheckboxChange(draft.id, "isFavorite")}
                        className="form-checkbox h-5 w-5 text-yellow-500"
                      />
                    </td>
                    <td className="px-4 py-2 border-b text-center">
                      {/* Download button could be here if needed */}
                    </td>
                  </tr>
                ))
              ) : (
                !isLoading && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No drafts found.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 0 || isLoading || !userId}
            className={`py-2 px-4 rounded-md mb-2 sm:mb-0 ${
              currentPage > 0
                ? "bg-gray-500 text-white hover:bg-gray-600"
                : "bg-gray-300 text-gray-700 cursor-not-allowed"
            }`}
            aria-label="Previous Page"
          >
            Previous
          </button>
          <span className="text-gray-700 mb-2 sm:mb-0">Page {currentPage + 1}</span>
          <button
            onClick={handleNext}
            disabled={!nextToken || isLoading || !userId}
            className={`py-2 px-4 rounded-md ${
              nextToken
                ? "bg-gray-500 text-white hover:bg-gray-600"
                : "bg-gray-300 text-gray-700 cursor-not-allowed"
            }`}
            aria-label="Next Page"
          >
            Next
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default DraftList;
